import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
   RecordingGroupPermission,
   RecordingPermission,
} from '../../call-recording-extensions-list/models/recording-permissions.model';
import { RoleReportViewModel } from '../../report-permissions-list/models/report-permission.model';
import { RolePrivilegeFormValue } from '../models/role-detail-form';
import { RoleDetailView } from '../models/role-detail-view.model';
import { GetRoleDetail_role_roleReportsConnection_items } from '../queries/generated/GetRoleDetail';
import { State, roleDetailFeatureKey } from './role-detail.reducer';

export const roleDetailFeature = createFeatureSelector<State>(roleDetailFeatureKey);

export const getRole = createSelector(roleDetailFeature, (state) => {
   return state.role;
});

export const getRoleCallState = createSelector(roleDetailFeature, (state) => {
   return state.callState;
});

export const getPrivileges = createSelector(roleDetailFeature, (state) => {
   return state.privileges;
});

export const getRoleDetailExtensions = createSelector(roleDetailFeature, (state) => {
   return state.extensions;
});

export const getRoleDetailExtensionGroups = createSelector(roleDetailFeature, (state) => {
   return state.extensionGroups;
});

export const getRoleDetail = createSelector(
   getRole,
   getRoleDetailExtensions,
   getRoleDetailExtensionGroups,
   (roleData, extensions, extensionGroups): RoleDetailView | null => {
      if (!roleData) {
         return null;
      }

      const { roleCallRecordingExtensionsConnection, roleCallRecordingExtensionGroupsConnection, ...role } = roleData;

      const extensionRecordingPermissionsMap: { [key: number]: RecordingPermission } = {};

      const extensionGroupRecordingPermissionsMap: { [key: number]: RecordingGroupPermission } = {};

      for (const recordingPermission of roleCallRecordingExtensionsConnection!.items) {
         extensionRecordingPermissionsMap[recordingPermission.extension.extensionId] = {
            ...recordingPermission,
            id: recordingPermission.roleCallRecordingExtensionId,
            isDeleted: false,
         };
      }
      for (const recordingPermission of roleCallRecordingExtensionGroupsConnection!.items) {
         extensionGroupRecordingPermissionsMap[recordingPermission.extensionGroup.extensionGroupId] = {
            ...recordingPermission,
            id: recordingPermission.roleCallRecordingExtensionGroupId,
            isDeleted: false,
         };
      }

      const extensionPermissions: RecordingPermission[] = [];

      const extensionGroupPermissions: RecordingGroupPermission[] = [];

      for (const extension of extensions) {
         const permission = extensionRecordingPermissionsMap[extension.extensionId];
         let newExtensionPermission: RecordingPermission;

         if (permission) {
            newExtensionPermission = {
               extension,
               id: permission.id,
               isInclude: permission.isInclude,
               isDeleted: permission.isDeleted,
            };
         } else {
            newExtensionPermission = {
               extension,
               id: 0,
               isInclude: false,
               isDeleted: false,
            };
         }
         extensionPermissions.push(newExtensionPermission);
      }

      for (const extensionGroup of extensionGroups) {
         const permission = extensionGroupRecordingPermissionsMap[extensionGroup.extensionGroupId];
         let newExtensionGroupPermission: RecordingGroupPermission;

         if (permission) {
            newExtensionGroupPermission = {
               extensionGroup,
               id: permission.id,
               isInclude: permission.isInclude,
               isDeleted: permission.isDeleted,
            };
         } else {
            newExtensionGroupPermission = {
               extensionGroup,
               id: 0,
               isInclude: false,
               isDeleted: false,
            };
         }
         extensionGroupPermissions.push(newExtensionGroupPermission);
      }

      return {
         ...role,
         roleRecordingExtensions: {
            allExtensionsIsSelected: role.hasAllExtensionRecordings,
            allExtensionsIsSelectedDirty: false,
            extensionRecordingPermissions: extensionPermissions,
            extensionGroupRecordingPermissions: extensionGroupPermissions,
         },
         account: role.account,
         roleReports: role.roleReportsConnection?.items
            ? mapRoleReportsConnectionItems(role.roleReportsConnection.items)
            : null,
         rolePrivileges: role.rolePrivilegesConnection.items.reduce((acc, rolePrivilege) => {
            acc[rolePrivilege.privilegeId] = {
               rolePrivilegeId: rolePrivilege.rolePrivilegeId,
               roleId: rolePrivilege.roleId,
               accountId: rolePrivilege.accountId,
               privilegeId: rolePrivilege.privilegeId,
               isAdd: rolePrivilege.isAdd,
               isDelete: rolePrivilege.isDelete,
               isEdit: rolePrivilege.isEdit,
               isEnabled: rolePrivilege.isEnabled,
               isView: rolePrivilege.isView,
            };
            return acc;
         }, {} as { [privilegeId: string]: RolePrivilegeFormValue }),
      };
   }
);

function mapRoleReportsConnectionItems(items: GetRoleDetail_role_roleReportsConnection_items[]): RoleReportViewModel[] {
   return items
      .filter((i) => i.report.reportDefinition != null)
      .map((i): RoleReportViewModel => {
         return {
            reportId: i.report.reportDefinition!,
            roleId: i.roleId,
            roleReportId: i.roleReportId,
         };
      });
}
